import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import UserStatusIcon from "../UserModal/statusIcon";
import useDialogStyles from "./dialogStyles";

const selectMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  getContentAnchorEl: null,
};

const filterOptions = createFilterOptions({
  trim: true,
});

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid, ticket }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const classes = useDialogStyles({ width: 480 });
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const [msgTransfer, setMsgTransfer] = useState('');

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);

      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/", {
            params: { searchParam },
          });
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };

      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleMsgTransferChange = (event) => {
    setMsgTransfer(event.target.value);
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
  };

  const handleSaveTicket = async (e) => {
    // e.preventDefault();
    if (!ticketid) return;
    if (!selectedQueue || selectedQueue === "") return;
    setLoading(true);
    try {
      let data = {};

      // ✅ CORRIGIDO: Em transferências manuais (isTransfered=true), SEMPRE passar o userId selecionado
      // O backend é responsável por decidir se aplica randomização ou não
      data.userId = !selectedUser ? null : selectedUser.id;
      data.status = !selectedUser ? "pending" : "open";
      data.queueId = selectedQueue;
      data.msgTransfer = msgTransfer ? msgTransfer : null;
      data.isTransfered = true;

      await api.put(`/tickets/${ticketid}`, data);
      setLoading(false);
      history.push(`/tickets/`);
      handleClose();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };


  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      scroll="paper"
      classes={{ paper: classes.paper }}
      aria-labelledby="transfer-ticket-title"
    >
      <div className={classes.header}>
        <h2 id="transfer-ticket-title" className={classes.title}>
          {i18n.t("transferTicketModal.title")}
        </h2>
        {/* TODO i18n */}
        <p className={classes.subtitle}>
          Escolha o setor e, se quiser, quem vai receber a conversa.
        </p>
      </div>
      <DialogContent className={classes.content}>
        <div className={classes.field}>
          {/* TODO i18n */}
          <span className={classes.label}>Atendente (opcional)</span>
          <Autocomplete
            fullWidth
            getOptionLabel={(option) => `${option.name}`}
            onChange={(e, newValue) => {
              setSelectedUser(newValue);
              if (newValue != null && Array.isArray(newValue.queues)) {
                if (newValue.queues.length === 1) {
                  setSelectedQueue(newValue.queues[0].id);
                }
                setQueues(newValue.queues);

              } else {
                setQueues(allQueues);
                setSelectedQueue("");
              }
            }}
            options={options}
            filterOptions={filterOptions}
            freeSolo
            autoHighlight
            noOptionsText={i18n.t("transferTicketModal.noOptions")}
            loading={loading}
            renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={i18n.t("transferTicketModal.fieldLabel")}
                variant="outlined"
                size="small"
                autoFocus
                onChange={(e) => setSearchParam(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </div>
        <div className={classes.field}>
          <span className={classes.label}>
            {i18n.t("transferTicketModal.fieldQueueLabel")}
          </span>
          <Select
            fullWidth
            displayEmpty
            variant="outlined"
            margin="dense"
            value={selectedQueue}
            onChange={(e) => setSelectedQueue(e.target.value)}
            MenuProps={selectMenuProps}
            renderValue={(value) => {
              const queue = queues.find((q) => q.id === value);
              if (!queue) {
                return (
                  <span className={classes.placeholder}>
                    {i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                  </span>
                );
              }
              return queue.name;
            }}
          >
            {queues.map((queue) => (
              <MenuItem dense key={queue.id} value={queue.id}>
                {queue.name}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className={classes.field}>
          <span className={classes.label}>
            {i18n.t("transferTicketModal.msgTransfer")}
          </span>
          <TextField
            value={msgTransfer}
            onChange={handleMsgTransferChange}
            variant="outlined"
            multiline
            maxRows={5}
            minRows={4}
            fullWidth
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={handleClose}
          disabled={loading}
          className={classes.cancel}
        >
          {i18n.t("transferTicketModal.buttons.cancel")}
        </Button>
        <ButtonWithSpinner
          variant="contained"
          type="submit"
          color="primary"
          loading={loading}
          disabled={selectedQueue === ""}
          onClick={() => handleSaveTicket(selectedQueue)}
          className={classes.confirm}
        >
          {i18n.t("transferTicketModal.buttons.ok")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
